// client/src/components/pages/LexiStar/Reports/tripReportData.ts
//
// Everything the trip report prints (ST-00188 M4-R2; FR-WB-010), read from the
// APIs the rest of LexiStar uses so the report shows the same figures as the
// trip detail, replay and cost screens:
//
//   GET /trips/:id           trip_view + legs (replaced legs = the earlier plan),
//                            timeline, deviations, ticket matches, fuel   (required)
//   GET /replay?trip_id=     track, stops, idles, visits (dwell), stage bands,
//                            events, planned legs with geometry          (replay.view)
//   GET /trips/:id/cost      estimated cost + "how was this calculated"   (costs.view)
//   GET /messages?trip_id=   message log                                  (messages.view)
//   GET /routes/:id          pinned route geometry for the map (every plan's legs)
//
// Optional parts fail soft: the section says why it is empty (no permission,
// not available yet) and the report is still ready to print.

import { getTrip, getRoute } from '../../../../services/lexistarStatusBoard';
import { getTripReplay } from '../../../../services/lexistarReplay';
import { listMessages } from '../../../../services/lexistarNotifications';
import { normaliseReplay } from '../Replay/replayLogic';
import { getTripCost } from '../../../../services/lexistarCosts';
import { isObj, pickList, statusOf } from '../../../../services/lexistarReports';
import type { CostHow, CostLine, Loose, TripCost, TripLeg } from '../../../../types/lexistar-reports';

type Obj = Record<string, unknown>;

const num = (v: unknown): number | null =>
  v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v)) ? Number(v) : null;
const str = (v: unknown): string | null =>
  v === null || v === undefined || v === '' ? null : String(v);

// ── Trip cost (M4-C: GET /trips/:id/cost → { trip, label, stored, cost, how }; services/lexistarCosts) ──

function normaliseLine(l: Obj): CostLine {
  return {
    kind: str(l.kind) || 'other',
    label: str(l.label) || str(l.kind) || 'Other',
    qty: num(l.qty),
    unit: str(l.unit),
    rate: num(l.rate),
    amount_zar: num(l.amount_zar ?? l.amount),
    provenance: str(l.provenance),
    source: str(l.source),
    note: str(l.note),
  };
}

const sub = (o: unknown, k: string): Obj => (isObj(o) && isObj(o[k]) ? (o[k] as Obj) : {});

function normaliseHow(h: unknown): CostHow | null {
  if (!isObj(h)) return null;
  const inputs = sub(h, 'inputs');
  const fuel = sub(inputs, 'fuel');
  const distance = sub(inputs, 'distance');
  const time = sub(inputs, 'time');
  const basis = sub(h, 'basis');
  const tolls = sub(inputs, 'tolls');
  return {
    formula: Array.isArray(h.formula) ? h.formula.map(String) : str(h.formula) ? [String(h.formula)] : [],
    fuel: {
      litres: num(fuel.litres),
      basis: str(fuel.basis),
      provenance: str(fuel.provenance),
      price_per_l: num(fuel.price_per_l ?? sub(h, 'fuel_price').price_per_l),
    },
    distance: {
      km_total: num(distance.km_total),
      km_total_basis: str(distance.km_total_basis),
      km_loaded: num(distance.km_loaded),
      km_loaded_basis: str(distance.km_loaded_basis),
      rate_per_km: num(distance.rate_per_km ?? sub(h, 'rate_card').rate_per_km),
    },
    time: {
      hours: num(time.hours),
      driver_rate_per_h: num(time.driver_rate_per_h ?? sub(h, 'rate_card').driver_rate_per_h),
      basis: str(sub(basis, 'hours').basis),
    },
    tonnes: {
      value: num(sub(inputs, 'tonnes').net_receipt_t ?? sub(basis, 'tonnes').value),
      basis: str(sub(basis, 'tonnes').basis),
    },
    tolls: pickList<Obj>(h.tolls).map((t) => ({
      name: str(t.name) || str(t.plaza_id) || 'Toll plaza',
      fee: num(t.fee),
    })),
    toll_class: str(tolls.toll_class ?? sub(h, 'rate_card').toll_class),
    missing: Array.isArray(h.missing) ? h.missing.map(String) : [],
    stale: h.stale === true,
    current_total_zar: num(h.current_total_zar),
    rate_card_pinned: isObj(h.rate_card) ? h.rate_card.pinned === true : null,
    fuel_price_pinned: isObj(h.fuel_price) ? h.fuel_price.pinned === true : null,
  };
}

/** The cost block of GET /trips/:id/cost (null when there is nothing to show). */
export function normaliseCost(body: unknown): TripCost | null {
  if (!isObj(body)) return null;
  const c = isObj(body.cost) ? body.cost : isObj(body.trip_cost) ? body.trip_cost : null;
  if (!c) return null;
  return {
    state: str(c.state) || 'estimated',
    stored: body.stored !== false,
    lines: pickList<Obj>(c.lines).map(normaliseLine),
    total_zar: num(c.total_zar),
    cost_per_t: num(c.cost_per_t),
    cost_per_tkm: num(c.cost_per_tkm),
    net_receipt_t: num(c.net_receipt_t),
    km_loaded: num(c.km_loaded),
    computed_at: str(c.computed_at),
    toll_class: str(c.toll_class),
    how: normaliseHow(body.how),
  };
}

export type Replay = ReturnType<typeof normaliseReplay>;

/** Why an optional part is (not) there. */
export type Availability = 'ok' | 'forbidden' | 'unavailable' | 'skipped';

export interface RouteGeometry {
  key: string;
  routeId: string;
  version: number;
  feature: Loose | null;
  replaced: boolean;
}

export interface TripReportData {
  body: Loose;
  trip: Loose;
  legs: TripLeg[];
  deviations: Loose[];
  matches: Loose[];
  timeline: Loose[];
  fuel: Loose | null;
  track: unknown;
  replay: Replay | null;
  replayState: Availability;
  cost: TripCost | null;
  costState: Availability;
  messages: Loose[];
  messagesState: Availability;
  routes: RouteGeometry[];
}

const availabilityOf = (err: unknown): Availability =>
  statusOf(err) === 403 ? 'forbidden' : 'unavailable';

async function routeGeometry(routeId: string, version: number): Promise<Loose | null> {
  const res = (await getRoute(routeId)) as unknown;
  const versions = pickList<Loose>(res, 'versions');
  const route = isObj(res) && isObj(res.route) ? res.route : {};
  const v =
    versions.find((x) => Number(x.version) === version) ||
    versions.find((x) => Number(x.version) === Number(route.current_version)) ||
    versions[versions.length - 1];
  const g = v?.geometry;
  if (!isObj(g)) return null;
  return g.type === 'Feature' ? g : { type: 'Feature', properties: {}, geometry: g };
}

export async function loadTripReport(
  tripId: string,
  { canReplay, canCost, canMessages }: { canReplay: boolean; canCost: boolean; canMessages: boolean }
): Promise<TripReportData> {
  const body = ((await getTrip(tripId)) || {}) as Loose;
  const trip = (isObj(body.trip) ? body.trip : body) as Loose;
  const legs = pickList<TripLeg>(trip.legs).length ? pickList<TripLeg>(trip.legs) : pickList<TripLeg>(body.legs);

  // Every pinned route version any plan used (replaced legs included).
  const refs = new Map<string, { routeId: string; version: number; replaced: boolean }>();
  legs.forEach((l) => {
    if (!l.route_id || l.route_version === null || l.route_version === undefined) return;
    const key = `${l.route_id}:${l.route_version}`;
    const replaced = l.state === 'replaced';
    const cur = refs.get(key);
    refs.set(key, {
      routeId: String(l.route_id),
      version: Number(l.route_version),
      replaced: cur ? cur.replaced && replaced : replaced,
    });
  });

  const [replayR, costR, messagesR, routesR] = await Promise.allSettled([
    canReplay ? getTripReplay(tripId, { tolerance_m: 25 }) : Promise.resolve(null),
    canCost ? getTripCost(tripId).then(normaliseCost) : Promise.resolve(null),
    canMessages ? listMessages({ trip_id: tripId, limit: 200 }) : Promise.resolve(null),
    Promise.allSettled(
      [...refs.entries()].map(async ([key, r]) => ({
        key,
        ...r,
        feature: await routeGeometry(r.routeId, r.version),
      }))
    ),
  ]);

  const replay =
    replayR.status === 'fulfilled' && replayR.value ? normaliseReplay(replayR.value as Loose) : null;
  const routes =
    routesR.status === 'fulfilled'
      ? routesR.value
          .filter((r): r is PromiseFulfilledResult<RouteGeometry> => r.status === 'fulfilled')
          .map((r) => r.value)
      : [];

  return {
    body,
    trip,
    legs,
    deviations: pickList<Loose>(body, 'deviations'),
    matches: pickList<Loose>(body, 'ticket_matches'),
    timeline: pickList<Loose>(body, 'timeline'),
    fuel: isObj(body.fuel) ? body.fuel : null,
    track: body.track ?? null,
    replay,
    replayState: !canReplay ? 'skipped' : replayR.status === 'fulfilled' ? 'ok' : availabilityOf(replayR.reason),
    cost: costR.status === 'fulfilled' ? costR.value : null,
    costState: !canCost ? 'skipped' : costR.status === 'fulfilled' ? 'ok' : availabilityOf(costR.reason),
    messages: messagesR.status === 'fulfilled' ? pickList<Loose>(messagesR.value, 'messages') : [],
    messagesState: !canMessages
      ? 'skipped'
      : messagesR.status === 'fulfilled'
        ? 'ok'
        : availabilityOf(messagesR.reason),
    routes,
  };
}
